/**
 * Engine Parameters Popover Component
 *
 * Icon button that opens a popover showing engine-specific parameters
 * for the currently selected model. Disabled for models with no params.
 */

import { SlidersHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { EngineParamsForm } from './EngineParamsForm';
import {
  ProviderParams,
  modelHasProviderParams,
  formatModelName,
} from '../types/image-generator.types';

export interface EngineParamsPopoverProps {
  modelName: string;
  providerParams: ProviderParams;
  onUpdate: (params: ProviderParams) => void;
  disabled?: boolean;
}

function hasConfiguredParams(providerParams: ProviderParams): boolean {
  return Object.values(providerParams).some(
    (v) => v !== '' && v !== undefined && v !== false && v !== null,
  );
}

export function EngineParamsPopover({
  modelName,
  providerParams,
  onUpdate,
  disabled = false,
}: EngineParamsPopoverProps) {
  const hasParams = modelHasProviderParams(modelName);
  const hasValues = hasConfiguredParams(providerParams);

  if (!hasParams) {
    return (
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        disabled
        title="No engine-specific settings for this model"
      >
        <SlidersHorizontal className="h-4 w-4" />
      </Button>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 relative"
          disabled={disabled}
          title="Engine parameters"
        >
          <SlidersHorizontal className="h-4 w-4" />
          {hasValues && (
            <span className="absolute top-0.5 right-0.5 h-1.5 w-1.5 rounded-full bg-primary" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80">
        <div className="space-y-3">
          <h4 className="text-sm font-medium">{formatModelName(modelName)} Parameters</h4>
          <EngineParamsForm
            modelName={modelName}
            providerParams={providerParams}
            onUpdate={onUpdate}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
